/**
 * The in-app source pane: a source link (OCEAN's "View original source", What's
 * New's "View source") opens beside the app instead of taking the reader away.
 *
 *   desktop  a pane from the right, ~56% wide, resizable from its left edge,
 *            the page dimmed behind it (click it to close)
 *   phone    a sheet rising from the bottom to 92% of the height, with a drag
 *            handle: swipe it down, or tap above it, to close
 *   header   title, domain, "Open in new tab", close; Escape closes
 *
 * Many sites refuse to be framed (X-Frame-Options / CSP frame-ancestors) and
 * the browser cannot tell the page so; GET /api/v1/frame-check
 * (Backend/frame_check.py) reads the headers first. A site that refuses, a
 * check that fails, or a frame that has not loaded within FRAME_TIMEOUT_MS
 * shows a card with "Open in new tab" — never an empty frame.
 *
 * Use: `openSource({ url, title, surface, track, props })` from anywhere, or
 * `useSourcePane().openSource(...)`. A <SourcePaneProvider> mounts the pane in
 * its tree; without one, the first call mounts it at the end of <body>.
 *
 * Tracking goes through the caller's own `track` (ocTrack for OCEAN, What's
 * New's preview-aware track), every event a `utech_click` with these actions:
 *   source_open          {surface, domain, ...props}
 *   source_blocked       {surface, domain, reason, ...props}
 *   source_open_new_tab  {surface, domain, from: header|card, state, ...props}
 *   source_close         {surface, domain, seconds_open, how, state, ...props}
 *   source_resize        {surface, domain, width_pct, ...props}
 */
import React, { useCallback, useEffect, useLayoutEffect, useRef, useState, useSyncExternalStore } from 'react';
import { createPortal } from 'react-dom';
import { createRoot } from 'react-dom/client';
import { apiService } from '../../services/apiService';

export type SourceTrack = (action: string, props: Record<string, unknown>) => void;

export interface SourceRequest {
  url: string;
  title?: string;
  /** Where the link was: 'ocean_problem', 'whatsnew', … (sent with every event). */
  surface: string;
  /** The surface's own tracker; nothing is tracked without it. */
  track?: SourceTrack;
  /** Extra event properties, e.g. {card_id, card_kind} or {item_id, category}. */
  props?: Record<string, unknown>;
}

const FRAME_TIMEOUT_MS = 12000;
const CHECK_TIMEOUT_MS = 7000;
const WIDTH_KEY = 'utech_source_pane_w';
const MOBILE_QUERY = '(max-width: 767px)';

export const domainOf = (u: string): string => {
  try { return new URL(u).hostname.replace(/^www\./, ''); } catch { return ''; }
};

// ---------------------------------------------------------------- store
interface Open extends SourceRequest { key: number; openedAt: number }
let current: Open | null = null;
let seq = 0;
const listeners = new Set<() => void>();
const emit = () => listeners.forEach(l => l());
const subscribe = (l: () => void) => { listeners.add(l); return () => { listeners.delete(l); }; };
const snapshot = () => current;

let hosts = 0;
let autoHost: HTMLDivElement | null = null;

export function openSource(req: SourceRequest): void {
  if (!req.url || !/^https?:\/\//i.test(req.url)) return;
  if (typeof document === 'undefined') return;
  if (!hosts && !autoHost) {
    autoHost = document.createElement('div');
    autoHost.setAttribute('data-source-pane-host', '');
    document.body.appendChild(autoHost);
    createRoot(autoHost).render(<SourcePaneHost portal={false} />);
  }
  current = { ...req, key: ++seq, openedAt: Date.now() };
  emit();
}

export function closeSource(): void {
  if (!current) return;
  current = null;
  emit();
}

export function useSourcePane(): { openSource: typeof openSource; closeSource: typeof closeSource } {
  return { openSource, closeSource };
}

/** Mounts the pane inside the app's tree (optional; see the file comment). */
export const SourcePaneProvider: React.FC<{ children?: React.ReactNode }> = ({ children }) => (
  <>{children}<SourcePaneHost portal /></>
);

// ---------------------------------------------------------------- frame check
interface Check { frameable: boolean; final_url: string | null; reason: string | null; content_type?: string | null }
const checks = new Map<string, Check>();

async function frameCheck(url: string): Promise<Check> {
  const hit = checks.get(url);
  if (hit) return hit;
  const ask = async (): Promise<Response> => {
    const ctl = new AbortController();
    const t = setTimeout(() => ctl.abort(), CHECK_TIMEOUT_MS);
    try {
      const uid = apiService.getStoredUserId() || '';
      const token = apiService.getStoredAccessToken() || '';
      const qs = new URLSearchParams({ url, user_id: uid });
      return await fetch(`${apiService.getApiBaseUrl()}/api/v1/frame-check?${qs.toString()}`, {
        headers: { Accept: 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        mode: 'cors', credentials: 'omit', signal: ctl.signal,
      });
    } finally { clearTimeout(t); }
  };
  try {
    let res = await ask();
    if (res.status === 401) {
      const fn = (apiService as any).refreshAccessToken;
      if (typeof fn === 'function' && await fn.call(apiService).catch(() => false)) res = await ask();
    }
    if (!res.ok) return { frameable: false, final_url: null, reason: `check_${res.status}` };
    const j = await res.json();
    const out: Check = { frameable: !!j.frameable, final_url: j.final_url || null, reason: j.reason || null,
                         content_type: j.content_type || null };
    checks.set(url, out);
    return out;
  } catch {
    return { frameable: false, final_url: null, reason: 'check_failed' };
  }
}

// ---------------------------------------------------------------- the pane
function useMedia(q: string): boolean {
  const get = () => (typeof window !== 'undefined' && window.matchMedia ? window.matchMedia(q).matches : false);
  const [m, setM] = useState(get);
  useEffect(() => {
    if (!window.matchMedia) return;
    const mq = window.matchMedia(q);
    const on = () => setM(mq.matches);
    on();
    mq.addEventListener ? mq.addEventListener('change', on) : mq.addListener(on);
    return () => { mq.removeEventListener ? mq.removeEventListener('change', on) : mq.removeListener(on); };
  }, [q]);
  return m;
}

const SourcePaneHost: React.FC<{ portal: boolean }> = ({ portal }) => {
  const open = useSyncExternalStore(subscribe, snapshot, snapshot);
  useEffect(() => {
    if (!portal) return;
    hosts += 1;
    return () => { hosts -= 1; };
  }, [portal]);
  // The body-level host steps aside once a provider's host exists.
  if (!open || (!portal && hosts > 0)) return null;
  const pane = <Pane key={open.key} req={open} />;
  return portal && typeof document !== 'undefined' ? createPortal(pane, document.body) : pane;
};

type State = 'checking' | 'loading' | 'loaded' | 'blocked';
type How = 'button' | 'escape' | 'backdrop' | 'swipe';

const IconExternal = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"
       strokeLinejoin="round" aria-hidden="true">
    <path d="M14 4h6v6" /><path d="M20 4l-9 9" /><path d="M18 14v5a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1h5" />
  </svg>
);
const IconClose = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"
       aria-hidden="true"><path d="M6 6l12 12M18 6L6 18" /></svg>
);
const Spinner = () => (
  <svg className="animate-spin h-5 w-5 text-primary-600" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" opacity="0.25" />
    <path d="M12 2a10 10 0 0 1 10 10" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
  </svg>
);

const clampWidth = (px: number): number => {
  const vw = window.innerWidth;
  return Math.round(Math.max(Math.min(380, vw), Math.min(px, vw * 0.92)));
};
const initialWidth = (): number => {
  let frac = 0.56;
  try { const v = parseFloat(localStorage.getItem(WIDTH_KEY) || ''); if (v > 0.2 && v < 0.95) frac = v; } catch { /* storage blocked */ }
  return clampWidth(Math.max(window.innerWidth * frac, 480));
};

const Pane: React.FC<{ req: Open }> = ({ req }) => {
  const mobile = useMedia(MOBILE_QUERY);
  const domain = domainOf(req.url);
  const [state, setState] = useState<State>('checking');
  const [src, setSrc] = useState(req.url);
  const [shown, setShown] = useState(false);       // entry animation
  const [leaving, setLeaving] = useState(false);
  const [width, setWidth] = useState(initialWidth);
  const [dragY, setDragY] = useState(0);
  const [resizing, setResizing] = useState(false);
  const stateRef = useRef<State>('checking');
  stateRef.current = state;
  const closeBtn = useRef<HTMLButtonElement>(null);
  const done = useRef(false);

  const track = useCallback((action: string, extra?: Record<string, unknown>) => {
    try { req.track?.(action, { surface: req.surface, domain, ...(req.props || {}), ...(extra || {}) }); }
    catch { /* never break a click */ }
  }, [req, domain]);

  const blocked = useCallback((reason: string) => {
    if (stateRef.current === 'blocked') return;
    setState('blocked');
    track('source_blocked', { reason });
  }, [track]);

  // Open: track, then ask whether the page may be framed.
  useEffect(() => {
    track('source_open');
    const prev = document.activeElement as HTMLElement | null;
    const raf = requestAnimationFrame(() => setShown(true));
    closeBtn.current?.focus({ preventScroll: true });
    let alive = true;
    frameCheck(req.url).then(c => {
      if (!alive) return;
      const target = c.final_url || req.url;
      const insecure = window.location.protocol === 'https:' && target.startsWith('http:');
      const pdfOnPhone = window.matchMedia?.(MOBILE_QUERY).matches && c.content_type === 'application/pdf';
      if (!c.frameable) blocked(c.reason || 'refused');
      else if (insecure) blocked('insecure');
      else if (pdfOnPhone) blocked('pdf');
      else { setSrc(target); setState('loading'); }
    });
    return () => { alive = false; cancelAnimationFrame(raf); try { prev?.focus?.({ preventScroll: true }); } catch { /* gone */ } };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // A frame that never reports loaded is treated as refused.
  useEffect(() => {
    if (state !== 'loading') return;
    const t = setTimeout(() => blocked('timeout'), FRAME_TIMEOUT_MS);
    return () => clearTimeout(t);
  }, [state, blocked]);

  const close = useCallback((how: How) => {
    if (done.current) return;
    done.current = true;
    track('source_close', { how, state: stateRef.current, seconds_open: Math.round((Date.now() - req.openedAt) / 1000) });
    setLeaving(true);
    setTimeout(() => { if (current?.key === req.key) closeSource(); }, 200);
  }, [track, req]);

  // Escape closes the pane first (capture phase, so a card under it stays open).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.preventDefault();
      e.stopPropagation();
      close('escape');
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [close]);

  const newTab = (from: 'header' | 'card') => track('source_open_new_tab', { from, state: stateRef.current });

  // ---- desktop: resize from the left edge
  const startResize = (e: React.PointerEvent) => {
    e.preventDefault();
    const startX = e.clientX, startW = width;
    let last = startW;
    setResizing(true);
    const move = (ev: PointerEvent) => { last = clampWidth(startW + (startX - ev.clientX)); setWidth(last); };
    const up = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      setResizing(false);
      const frac = last / window.innerWidth;
      try { localStorage.setItem(WIDTH_KEY, frac.toFixed(3)); } catch { /* storage blocked */ }
      if (Math.abs(last - startW) > 8) track('source_resize', { width_pct: Math.round(frac * 100) });
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
  };
  const onResizeKey = (e: React.KeyboardEvent) => {
    if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
    e.preventDefault();
    const next = clampWidth(width + (e.key === 'ArrowLeft' ? 40 : -40));
    setWidth(next);
    try { localStorage.setItem(WIDTH_KEY, (next / window.innerWidth).toFixed(3)); } catch { /* storage blocked */ }
  };

  // ---- phone: drag the sheet down by its handle / header. Window listeners and
  // a cover over the frame while dragging, so the frame never swallows the moves.
  const [dragging, setDragging] = useState(false);
  const onSheetDown = (e: React.PointerEvent) => {
    if ((e.target as HTMLElement).closest('a,button')) return;
    const y0 = e.clientY, t0 = Date.now(), id = e.pointerId;
    let dy = 0;
    setDragging(true);
    const move = (ev: PointerEvent) => { if (ev.pointerId !== id) return; dy = Math.max(0, ev.clientY - y0); setDragY(dy); };
    const up = (ev: PointerEvent) => {
      if (ev.pointerId !== id) return;
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      window.removeEventListener('pointercancel', up);
      setDragging(false);
      const v = dy / Math.max(1, Date.now() - t0);
      if (ev.type === 'pointerup' && (dy > 120 || (dy > 30 && v > 0.6))) close('swipe');
      else setDragY(0);
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
    window.addEventListener('pointercancel', up);
  };

  // Keep the page behind still while the pane is open.
  useLayoutEffect(() => {
    const b = document.body, prev = b.style.overflow;
    b.style.overflow = 'hidden';
    return () => { b.style.overflow = prev; };
  }, []);

  const visible = shown && !leaving;
  const title = req.title?.trim() || domain || 'Source';

  const header = (
    <div className="flex items-center gap-3 px-4 py-3 border-b border-grayscale-200 bg-white">
      <div className="min-w-0 flex-1">
        <div className="text-sm font-semibold text-grayscale-1300 truncate" title={title}>{title}</div>
        {domain && <div className="text-xs text-grayscale-1000 truncate">{domain}</div>}
      </div>
      <a href={src} target="_blank" rel="noopener noreferrer nofollow" onClick={() => newTab('header')}
         className="inline-flex items-center gap-1.5 shrink-0 h-9 px-3 rounded-lg border border-primary-300 text-sm font-medium text-primary-700 hover:bg-primary-100 transition-colors"
         aria-label="Open in new tab" title="Open in new tab">
        <IconExternal /><span className={mobile ? 'sr-only' : ''}>Open in new tab</span>
      </a>
      <button ref={closeBtn} type="button" onClick={() => close('button')} aria-label="Close source"
              className="shrink-0 w-9 h-9 inline-flex items-center justify-center rounded-full text-grayscale-1100 hover:bg-primary-100 hover:text-grayscale-1300 transition-colors">
        <IconClose />
      </button>
    </div>
  );

  const body = (
    <div className="relative flex-1 min-h-0 bg-white">
      {(state === 'loading' || state === 'loaded') && (
        <iframe key={src} src={src} title={title} onLoad={() => setState(s => (s === 'loading' ? 'loaded' : s))}
                className={`absolute inset-0 w-full h-full border-0 bg-white ${resizing ? 'pointer-events-none' : ''}`}
                sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-popups-to-escape-sandbox allow-presentation"
                referrerPolicy="no-referrer" allow="fullscreen; clipboard-write" />
      )}
      {(state === 'checking' || state === 'loading') && (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-white text-sm text-grayscale-1100"
             role="status" aria-live="polite">
          <Spinner />
          <span>Loading preview{domain ? ` of ${domain}` : ''}…</span>
        </div>
      )}
      {state === 'blocked' && (
        <div className="absolute inset-0 flex items-center justify-center p-6 bg-white">
          <div className="max-w-sm w-full text-center rounded-xl border border-primary-200 bg-primary-100/30 px-6 py-8 flex flex-col items-center gap-3">
            <span className="w-11 h-11 rounded-full bg-primary-100 text-primary-700 inline-flex items-center justify-center">
              <IconExternal />
            </span>
            <div className="text-base font-semibold text-grayscale-1300">This site doesn’t allow previews here</div>
            <p className="m-0 text-sm text-grayscale-1000">
              {domain ? <><span className="font-medium text-grayscale-1200">{domain}</span> opens in a new tab instead.</> : 'It opens in a new tab instead.'}
            </p>
            <a href={src} target="_blank" rel="noopener noreferrer nofollow" onClick={() => newTab('card')}
               className="mt-1 inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-primary-500 hover:bg-primary-600 text-white text-sm font-medium transition-colors">
              <IconExternal />Open in new tab
            </a>
          </div>
        </div>
      )}
    </div>
  );

  if (mobile) {
    return (
      <div className="fixed inset-0 z-[80]" role="presentation">
        <div className={`absolute inset-0 bg-black/40 transition-opacity duration-200 ${visible ? 'opacity-100' : 'opacity-0'}`}
             onClick={() => close('backdrop')} />
        <div role="dialog" aria-modal="true" aria-label={title}
             className="absolute inset-x-0 bottom-0 h-[92dvh] max-h-[92vh] flex flex-col bg-white rounded-t-2xl shadow-[0_-12px_40px_rgba(16,24,40,0.22)] overflow-hidden"
             style={{ transform: visible ? `translateY(${dragY}px)` : 'translateY(100%)',
                      transition: dragging ? 'none' : 'transform 200ms ease-out' }}>
          <div className="touch-none select-none cursor-grab" onPointerDown={onSheetDown}>
            <div className="flex justify-center pt-2 pb-1 bg-white">
              <span className="w-10 h-1.5 rounded-full bg-grayscale-600/60" aria-hidden="true" />
            </div>
            {header}
          </div>
          {body}
          {dragging && <div className="absolute inset-0 z-10" />}
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-[80]" role="presentation">
      <div className={`absolute inset-0 bg-black/30 transition-opacity duration-200 ${visible ? 'opacity-100' : 'opacity-0'}`}
           onClick={() => close('backdrop')} />
      <div role="dialog" aria-modal="true" aria-label={title}
           className="absolute right-0 top-0 bottom-0 flex flex-col bg-white shadow-[-18px_0_44px_rgba(16,24,40,0.18)]"
           style={{ width, transform: visible ? 'translateX(0)' : 'translateX(100%)', transition: 'transform 200ms ease-out' }}>
        <div role="separator" aria-orientation="vertical" aria-label="Resize the source pane" tabIndex={0}
             onPointerDown={startResize} onKeyDown={onResizeKey}
             className="group absolute left-0 top-0 bottom-0 -translate-x-1/2 w-3 cursor-col-resize z-10 flex items-center justify-center focus:outline-none">
          <span className="w-1 h-10 rounded-full bg-grayscale-600/50 group-hover:bg-primary-500 group-focus-visible:bg-primary-500 transition-colors" />
        </div>
        {header}
        {body}
      </div>
      {resizing && <div className="absolute inset-0 cursor-col-resize" />}
    </div>
  );
};
